import {
  Bar, CartesianGrid, ComposedChart, LabelList, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

// Fixed amber colour for all one-time revenue — stands out against any palette
const ONE_TIME_COLOR = "#f59e0b";
const ONE_TIME_KEY = "__one_time__";
const ONE_TIME_LABEL = "One-time";

const COMPARE_KEY = "__compare__";

export default function ReportChart({
  report, settings, palette, viewMode, onMonthClick, formatCurrency, formatMonthLabel,
  // A previous fiscal year's monthly totals, month one against month one,
  // drawn as a dashed line over this year's bars.
  comparison = null, comparisonLabel = null,
}) {
  // Does any month have one-time revenue?
  const hasOneTime = (report?.months || []).some(
    (m) => m.one_time_products && Object.values(m.one_time_products).some((v) => v > 0)
  );

  const chartData = (report?.months || []).map((month, index) => {
    // Sum all one-time products into a single "One-time" band
    const otTotal = Object.values(month.one_time_products || {}).reduce((s, v) => s + v, 0);
    return {
      label: formatMonthLabel(month.month),
      month: month.month,
      total_mrr: month.total_mrr,
      ...month.products,
      ...(hasOneTime && otTotal > 0 ? { [ONE_TIME_KEY]: otTotal } : {}),
      ...(comparison && comparison[index] !== undefined ? { [COMPARE_KEY]: comparison[index] } : {}),
    };
  });

  const handleChartClick = (state) => {
    const rawMonth = (state?.activePayload || []).find((entry) => entry?.payload?.month)?.payload?.month;
    if (rawMonth) onMonthClick(rawMonth);
  };

  const handleBarClick = (entry) => {
    const rawMonth = entry?.month || entry?.payload?.month;
    if (rawMonth) onMonthClick(rawMonth);
  };

  return (
    <div className="w-full min-w-0 rounded-md border border-[#cccccc] bg-white p-4" data-testid="mrr-chart-wrapper">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3" data-testid="chart-toolbar">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Monthly MRR by product</p>
        <p className="text-xs text-[#4a5b68] print:hidden" data-testid="chart-toolbar-caption">Click a month to open the contributing record drawer.</p>
      </div>
      <ResponsiveContainer width="100%" height={390} minWidth={320} minHeight={390} debounce={120}>
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 16, left: -12, bottom: 0 }}
          onClick={handleChartClick}
        >
          <CartesianGrid stroke="#dddddd" vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#4a5b68", fontSize: 12 }} />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: "#4a5b68", fontSize: 12 }}
            tickFormatter={(value) => formatCurrency(value, true)}
          />
          <Tooltip
            contentStyle={{ borderRadius: 4, borderColor: "#cccccc", boxShadow: "0 4px 12px rgba(51, 71, 91, 0.12)" }}
            formatter={(value, name) => [
              formatCurrency(value),
              name === ONE_TIME_KEY ? ONE_TIME_LABEL
                : name === COMPARE_KEY ? (comparisonLabel || "Last year") : name,
            ]}
            labelStyle={{ color: "#323c44", fontWeight: 700 }}
          />
          {settings.showLegend ? (
            <Legend
              formatter={(value) =>
                value === ONE_TIME_KEY ? ONE_TIME_LABEL
                  : value === COMPARE_KEY ? (comparisonLabel || "Last year") : value}
              wrapperStyle={{ fontSize: 12, color: "#4a5b68" }}
            />
          ) : null}

          {/* Recurring product bars — palette colours */}
          {report.products.map((product, index) => (
            <Bar
              key={product}
              dataKey={product}
              stackId={viewMode === "stacked" ? "mrr" : undefined}
              fill={palette[index % palette.length]}
              radius={hasOneTime ? [0, 0, 0, 0] : [2, 2, 0, 0]}
              maxBarSize={42}
              cursor="pointer"
              onClick={handleBarClick}
              data-testid={`chart-bar-${product}`}
            />
          ))}

          {/* One-time band — fixed amber, always on top of recurring stack */}
          {hasOneTime && (
            <Bar
              key={ONE_TIME_KEY}
              dataKey={ONE_TIME_KEY}
              stackId={viewMode === "stacked" ? "mrr" : undefined}
              fill={ONE_TIME_COLOR}
              radius={[2, 2, 0, 0]}
              maxBarSize={42}
              cursor="pointer"
              onClick={handleBarClick}
              data-testid="chart-bar-one-time"
            />
          )}

          {settings.showTotals ? (
            <Bar dataKey="total_mrr" fill="transparent" isAnimationActive={false} legendType="none" cursor="pointer" onClick={handleBarClick}>
              <LabelList
                dataKey="total_mrr"
                position="top"
                fill="#323c44"
                fontSize={12}
                formatter={(value) => formatCurrency(value, true)}
              />
            </Bar>
          ) : null}

          {comparison && (
            <Line
              type="monotone"
              dataKey={COMPARE_KEY}
              stroke="#7d8b96"
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={false}
              isAnimationActive={false}
              name={COMPARE_KEY}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
